import React, { useEffect, useRef, useState } from "react";
import Highcharts from "highcharts";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  Card,
  ChartContainer,
  LegendChip,
  LegendDot,
  LegendRow,
  SummaryItem,
  SummaryLabel,
  SummaryRow,
  SummaryValue
} from "./styled";
import { useUsersSlice } from "../slice/index";
import { selectCreatorAnalytics, selectCreatorChart } from "../slice/selectors";
import LoadingSpinner from "components/PageLoader/Spinner";

import { ACTION_COLORS, FALLBACK_COLORS } from "../data";

const normalize = (action) =>
  action === "call" || action === "whatsapp" ? "contact" : action;

// ── Explicit display orders ───────────────────────────────────────────────
// Creator Stats: Contact (call + whatsapp) → Search → Share → View
// These keys are ALWAYS shown — even if absent in API response (value = 0)
const CREATOR_STATS_ORDER = ["contact", "search", "share", "view"];

const ANALYTICS_ONLY_LABEL_MAP = {
  contact: "Contact (call + whatsapp)"
};

const getLabel = (key) =>
  ANALYTICS_ONLY_LABEL_MAP[key] ?? key.charAt(0).toUpperCase() + key.slice(1);

const CreatorStats = () => {
  const { userId } = useParams();
  const dispatch = useDispatch();
  const { actions } = useUsersSlice();
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const { data: analyticsData, loading: analyticsLoading } = useSelector(
    selectCreatorAnalytics
  );
  const { data: chartData, loading: chartLoading } =
    useSelector(selectCreatorChart);

  const isLoading = analyticsLoading || chartLoading;

  // null = show all; string = solo-active key
  const [soloKey, setSoloKey] = useState(null);

  useEffect(() => {
    if (!userId) return;
    dispatch(actions.fetchCreatorAnalytics({ userId }));
    dispatch(actions.fetchCreatorChart({ userId }));
  }, [userId]);

  // ── Derive months & series from chartData ─────────────────────────────
  const months = [...new Set((chartData || []).map((r) => r.month))];

  const seriesMap = {};
  (chartData || []).forEach(({ action, month, count }) => {
    const key = normalize(action);
    if (!seriesMap[key]) seriesMap[key] = {};
    seriesMap[key][month] = (seriesMap[key][month] || 0) + count;
  });

  const dynamicKeys = Object.keys(seriesMap);

  // ── allSeriesKeys: always includes CREATOR_STATS_ORDER keys + any extras from data
  // This ensures legend buttons are ALWAYS rendered even when chartData is empty
  const extraKeys = dynamicKeys.filter((k) => !CREATOR_STATS_ORDER.includes(k));
  const allSeriesKeys = [...CREATOR_STATS_ORDER, ...extraKeys];

  // Assign colors: known ones from ACTION_COLORS map, extras from fallback palette
  let fallbackIndex = 0;
  const getColor = (key) =>
    ACTION_COLORS[key] ??
    FALLBACK_COLORS[fallbackIndex++ % FALLBACK_COLORS.length];

  // allSeries: always built from allSeriesKeys (never empty → legend always shows)
  const allSeries = allSeriesKeys.map((key) => ({
    key,
    name: key.charAt(0).toUpperCase() + key.slice(1),
    color: getColor(key),
    // 0 when month has no data for this key
    data: months.length
      ? months.map((m) => seriesMap[key]?.[m] ?? null)
      : [null], // ← ?? null
    connectNulls: false
  }));

  // ── Summary row: always show CREATOR_STATS_ORDER keys + extras, value = 0 fallback
  const analyticsKeys = Object.keys(analyticsData || {});
  const MERGED_INTO_CONTACT = ["call", "whatsapp"];

  const extraSummaryKeys = [
    ...new Set([...analyticsKeys, ...dynamicKeys])
  ].filter(
    (k) => !CREATOR_STATS_ORDER.includes(k) && !MERGED_INTO_CONTACT.includes(k)
  );

  const sortedSummaryKeys = [...CREATOR_STATS_ORDER, ...extraSummaryKeys];

  const getMergedCreatorValue = (key) => {
    if (!analyticsData) return 0;
    if (key === "contact") {
      const contactFromChart = seriesMap["contact"]
        ? Object.values(seriesMap["contact"]).reduce((a, b) => a + b, 0)
        : 0;
      return contactFromChart;
    }
    return Number(analyticsData?.[key] ?? 0);
  };

  const statsSummary = sortedSummaryKeys.map((key) => ({
    label: getLabel(key),
    value: getMergedCreatorValue(key)
  }));

  // Chart series: use allSeries but only chart-renderable ones (skip contact which has no chart line)
  // contact is analytics-only — exclude it from chart series if it has no chartData entries
  const chartOnlyKeys =
    dynamicKeys.length > 0
      ? allSeriesKeys.filter((k) => dynamicKeys.includes(k))
      : allSeriesKeys;

  const chartSeries =
    chartOnlyKeys.length > 0
      ? allSeries.filter((s) => chartOnlyKeys.includes(s.key))
      : [
          {
            name: "Empty",
            data: months.length ? months.map(() => 0) : [0],
            color: "#e5e7eb",
            enableMouseTracking: false,
            showInLegend: false
          }
        ];

  // ── Build chart when chartData arrives ────────────────────────────────
  useEffect(() => {
    if (!chartRef.current) return;
    if (isLoading) return;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    chartInstanceRef.current = Highcharts.chart(chartRef.current, {
      chart: {
        type: "line",
        backgroundColor: "transparent",
        style: { fontFamily: "inherit" },
        marginTop: 30
      },
      title: {
        text: "User Creator Insights",
        style: { fontSize: "14px", fontWeight: "600", color: "#374151" }
      },
      xAxis: {
        categories: months.length ? months : [""],
        labels: { style: { color: "#9ca3af", fontSize: "12px" } },
        title: { text: "Month", style: { color: "#9ca3af", fontSize: "12px" } },
        lineColor: "#e5e7eb",
        tickColor: "transparent"
      },
      yAxis: {
        min: 0,
        allowDecimals: false,
        title: { text: "Count", style: { color: "#9ca3af", fontSize: "12px" } },
        labels: { style: { color: "#9ca3af", fontSize: "12px" } },
        gridLineColor: "#f3f4f6"
      },
      legend: { enabled: false },
      tooltip: {
        outside: true,
        useHTML: true,
        backgroundColor: "#fff",
        borderColor: "transparent",
        borderRadius: 8,
        borderWidth: 0,
        shadow: {
          color: "rgba(0,0,0,0.08)",
          offsetX: 0,
          offsetY: 4,
          opacity: 1,
          width: 12
        },
        padding: 12,
        style: { color: "#1f1f1f" },
        shape: "square",
        positioner: function (labelWidth, labelHeight, point) {
          const chart = this.chart;
          const x = chart.plotLeft + point.plotX - labelWidth / 2;
          const y = chart.plotTop + point.plotY - labelHeight - 25;
          const clampedX = Math.max(
            chart.plotLeft,
            Math.min(x, chart.plotLeft + chart.plotWidth - labelWidth)
          );
          return { x: clampedX, y };
        },
        formatter: function () {
          const point = this.point;
          const seriesName = this.series.name;

          if (point.y === null || point.y === undefined || point.y === 0)
            return "";

          return `
    <div style="display:flex;align-items:center;gap:8px;border-left:4px solid ${point.color};padding-left:8px;font-size:14px;font-weight:500;min-width:100px;">
      <div>
        <div style="font-size:12px;color:rgba(31,31,31,0.60);font-weight:500;line-height:16px;padding-bottom:8px;">${seriesName}</div>
        <div><span style="font-weight:500;color:#1F1F1F;font-size:20px;line-height:28px;">${point.y}</span></div>
      </div>
    </div>
  `;
        }
      },
      plotOptions: {
        line: {
          lineWidth: 2.5,
          marker: { enabled: true, radius: 4, symbol: "circle" },
          dataLabels: {
            enabled: true,
            style: {
              fontSize: "11px",
              fontWeight: "500",
              color: "#6b7280",
              textOutline: "none"
            },
            formatter: function () {
              return this.y !== null ? this.y : "";
            }
          }
        }
      },
      series: chartSeries.map((s) => ({
        name: s.name,
        color: s.color,
        data: s.data,
        connectNulls: s.connectNulls,
        visible: true
      })),
      credits: { enabled: false }
    });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [chartData, isLoading]);

  // ── Legend click: solo mode ───────────────────────────────────────────
  const handleLegendClick = (key) => {
    if (!chartInstanceRef.current) return;

    setSoloKey((prev) => {
      const isSolo = prev === key;
      const nextSolo = isSolo ? null : key;

      allSeriesKeys.forEach((k) => {
        const name = k.charAt(0).toUpperCase() + k.slice(1);
        const hcSeries = chartInstanceRef.current.series.find(
          (s) => s.name === name
        );
        if (hcSeries)
          hcSeries.setVisible(nextSolo === null || nextSolo === k, false);
      });
      chartInstanceRef.current.redraw();

      return nextSolo;
    });
  };

  return (
    <Card>
      <SummaryRow>
        {statsSummary?.map((item) => (
          <SummaryItem key={item.label}>
            <SummaryLabel>{item.label}</SummaryLabel>
            <SummaryValue>{item.value}</SummaryValue>
          </SummaryItem>
        ))}
      </SummaryRow>

      {isLoading ? (
        <LoadingSpinner
          color="#797979"
          text="Loading..."
          fontSize={14}
          size={16}
          className="loading-spinner"
        />
      ) : (
        <>
          <ChartContainer ref={chartRef} />

          {/* Legend always renders all CREATOR_STATS_ORDER buttons */}
          <LegendRow>
            {allSeries.map(({ key, name, color }) => {
              const isActive = soloKey === null || soloKey === key;
              return (
                <LegendChip
                  key={key}
                  color={color}
                  active={isActive}
                  onClick={() => handleLegendClick(key)}
                  title={soloKey === key ? "Show all" : `Show only ${name}`}
                >
                  <LegendDot color={color} active={isActive} />
                  {name}
                </LegendChip>
              );
            })}
          </LegendRow>
        </>
      )}
    </Card>
  );
};

export default CreatorStats;
